import { ArrowRight, Gift, Heart, Plane } from 'lucide-react';

interface Props {
  onGiftClick: (tier: 'premium_economy' | 'first_class') => void;
}

const TIERS = [
  {
    id: 'premium_economy' as const,
    name: 'Premium Economy',
    cta: 'Send Premium Economy Class Gift',
    rows: [
      { label: '1 Month Gift', price: '€6.99' },
      { label: '3 Months Gift', was: '€20.97', price: '€19.50', save: 'Save 7%' },
      { label: '6 Months Gift', was: '€41.94', price: '€36.49', save: 'Save 13%' },
      { label: '12 Months Gift', was: '€83.88', price: '€67.10', save: 'Save 20%' },
    ],
  },
  {
    id: 'first_class' as const,
    name: 'First Class',
    cta: 'Send First Class Gift',
    rows: [
      { label: '1 Month Gift', price: '€11.99' },
      { label: '3 Months Gift', was: '€35.97', price: '€33.45', save: 'Save 7%' },
      { label: '6 Months Gift', was: '€71.94', price: '€62.59', save: 'Save 13%' },
      { label: '12 Months Gift', was: '€143.88', price: '€115.10', save: 'Save 20%' },
    ],
  },
];

const STEPS = [
  { icon: Gift, title: '01 · Choose a Plan', text: 'Select the Perfect Subscription Duration and Tier for Your Recipient' },
  { icon: Heart, title: '02 · Send with Love', text: "We'll Send Them a Gift Notification via Email" },
  { icon: Plane, title: '03 · They Travel Happy', text: 'Instant Access to Premium Features for Their Next Adventure' },
];

export const GiftSection = ({ onGiftClick }: Props) => (
  <section id="gift-subscriptions" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 scroll-mt-24" aria-labelledby="gift-heading">
    <div className="grid lg:grid-cols-[1fr_1.15fr_1.15fr] gap-12 lg:gap-14">
      <div>
        <p className="text-sm font-semibold uppercase tracking-wide text-primary">Gift subscriptions</p>
        <h2 id="gift-heading" className="mt-5 text-4xl font-bold leading-tight text-foreground">
          For someone<br /><span className="text-primary">going somewhere.</span>
        </h2>
        <p className="mt-5 text-base text-muted-foreground">Give a little more peace of mind for their next journey.</p>
      </div>
      {TIERS.map((t) => (
        <div key={t.id}>
          <h3 className="text-2xl font-bold text-foreground pb-14 border-b border-border">{t.name}</h3>
          {t.rows.map((r) => (
            <div key={r.label} className="flex items-center justify-between gap-3 py-6 border-b border-border">
              <div>
                <p className="text-base text-foreground">{r.label}</p>
                {r.save && <p className="mt-1 text-sm font-semibold text-primary">{r.save}</p>}
              </div>
              <p className="flex items-baseline gap-3">
                {r.was && <span className="text-sm line-through text-muted-foreground">{r.was}</span>}
                <span className="text-3xl font-bold text-primary">{r.price}</span>
              </p>
            </div>
          ))}
          <button
            onClick={() => onGiftClick(t.id)}
            className="mt-8 inline-flex w-full items-center justify-center gap-3 min-h-[60px] rounded border border-border text-base font-semibold text-foreground hover:border-primary hover:text-primary transition-colors"
          >
            <Heart className="h-4 w-4" /> {t.cta} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>

    <div className="mt-16 pt-16 border-t border-border">
      <h3 className="text-2xl font-bold text-foreground mb-10">How Gift Subscriptions Work</h3>
      <div className="grid md:grid-cols-3 gap-10">
        {STEPS.map(({ icon: Icon, title, text }) => (
          <div key={title} className="flex gap-4">
            <Icon className="h-6 w-6 shrink-0 text-primary mt-0.5" />
            <div>
              <h4 className="text-lg font-semibold text-foreground">{title}</h4>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">{text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);
